import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { CurrencyProvider } from "@/context/CurrencyContext";
import { captureRefFromUrl } from "@/lib/referrals";

import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import AuthGuard from "@/components/AuthGuard";
import AfriToPlyxRibbon from "@/components/AfriToPlyxRibbon";

import Landing from "@/pages/Landing";
import PlyxLanding from "@/pages/PlyxLanding";
import PlyxFeed from "@/pages/plyx/PlyxFeed";
import PlyxLogin from "@/pages/plyx/PlyxLogin";
import PlyxRegister from "@/pages/plyx/PlyxRegister";
import PlyxAdminLogin from "@/pages/plyx/PlyxAdminLogin";
import PlyxForgot from "@/pages/plyx/PlyxForgot";
import PlyxReset from "@/pages/plyx/PlyxReset";
import PlyxUpload from "@/pages/plyx/PlyxUpload";
import PlyxProfile from "@/pages/plyx/PlyxProfile";
import PlyxSettings from "@/pages/plyx/PlyxSettings";
import PlyxWallet from "@/pages/plyx/PlyxWallet";
import PlyxShop from "@/pages/plyx/PlyxShop";
import PlyxShopManage from "@/pages/plyx/PlyxShopManage";
import PlyxChallenges from "@/pages/plyx/PlyxChallenges";
import PlyxChallengeDetail from "@/pages/plyx/PlyxChallengeDetail";
import PlyxChallengeNew from "@/pages/plyx/PlyxChallengeNew";
import PlyxLeaderboard from "@/pages/plyx/PlyxLeaderboard";
import PlyxMakers from "@/pages/plyx/PlyxMakers";
import PlyxMakerDetail from "@/pages/plyx/PlyxMakerDetail";
import ReferralLeaderboard from "@/pages/ReferralLeaderboard";
import PlyxHeader from "@/components/plyx/PlyxHeader";
import { PlyxAuthProvider } from "@/context/PlyxAuthContext";
import Browse from "@/pages/Browse";
import AIShopperPage from "@/pages/AIShopperPage";
import AIShopperBubble from "@/components/AIShopper";
import ListingDetail from "@/pages/ListingDetail";
import CreateListing from "@/pages/CreateListing";
import Dashboard from "@/pages/Dashboard";
import DeveloperKeys from "@/pages/DeveloperKeys";
import DeveloperDocs from "@/pages/DeveloperDocs";
import Messages from "@/pages/Messages";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Verify from "@/pages/Verify";
import Admin from "@/pages/Admin";
import FulfilOrders from "@/pages/FulfilOrders";
import Pro from "@/pages/Pro";
import MapView from "@/pages/MapView";
import LogoCandidates from "@/pages/LogoCandidates";
import PublicStats from "@/pages/PublicStats";
import AdminRevenue from "@/pages/AdminRevenue";
import AdminPlyxNps from "@/pages/AdminPlyxNps";
import AdminPlyxHealth from "@/pages/AdminPlyxHealth";
import AdminPlyxDashboard from "@/pages/AdminPlyxDashboard";
import AdminPlyxAudit from "@/pages/AdminPlyxAudit";
import Complaints from "@/pages/Complaints";
import ComplaintNew from "@/pages/ComplaintNew";
import ComplaintDetail from "@/pages/ComplaintDetail";
import AdminComplaints from "@/pages/AdminComplaints";
import BuyerGuarantee from "@/pages/BuyerGuarantee";
import ShareAd from "@/pages/ShareAd";
import CategoryLanding from "@/pages/CategoryLanding";
import LocationLanding from "@/pages/LocationLanding";
import LocationsIndex from "@/pages/LocationsIndex";
import NearMe from "@/pages/NearMe";
import Payouts from "@/pages/Payouts";
import Purchases from "@/pages/Purchases";
import Sales from "@/pages/Sales";
import TermsOfService from "@/pages/TermsOfService";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import CookiePolicy from "@/pages/CookiePolicy";
import Contact from "@/pages/Contact";
import OrderTrack from "@/pages/OrderTrack";
import Makers from "@/pages/Makers";
import MakerNew from "@/pages/MakerNew";
import MakerDetail from "@/pages/MakerDetail";
import Movies from "@/pages/Movies";
import MovieNew from "@/pages/MovieNew";
import MovieDetail from "@/pages/MovieDetail";
import Shops from "@/pages/Shops";
import ShopDetail from "@/pages/ShopDetail";
import ShopManage from "@/pages/ShopManage";
import InstallAppPrompt from "@/components/InstallAppPrompt";
import CookieConsentBanner from "@/components/CookieConsentBanner";
import NewsletterPopup from "@/components/NewsletterPopup";
import { isPlyxStandaloneMode } from "@/lib/plyxStandalone";
import PlyxSEO from "@/components/plyx/PlyxSEO";
import AfriMarketSEO from "@/components/AfriMarketSEO";

// Detect OAuth callback (session_id in URL fragment) before normal routing.
const AppRouter = () => {
  const location = useLocation();
  // Capture ?ref= attribution on every navigation (cheap localStorage write)
  useEffect(() => { captureRefFromUrl(); }, [location.pathname, location.search]);
  // GA4 SPA page-view tracking — fire on every route change.
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.gtag !== "function") return;
    window.gtag("event", "page_view", {
      page_path: location.pathname + location.search,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [location.pathname, location.search]);
  // CRITICAL: handle session_id during render to avoid race with ProtectedRoute
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  // Plyx standalone preview mode — the app behaves as if Plyx is the entire
  // product (all AfriMarket routes redirect to /plyx, cross-promo hidden).
  // Triggered by visiting any URL with ?standalone=plyx (persists in
  // localStorage). To exit: ?standalone=off
  const standalonePlyx = isPlyxStandaloneMode();

  // Plyx renders as a standalone brand experience — no AfriMarket navbar / footer / popups.
  const isPlyx = location.pathname.startsWith("/plyx") || standalonePlyx;
  // Feed is full-screen immersive — even Plyx header is suppressed on /plyx exactly.
  const isPlyxFeed = location.pathname === "/plyx" || (standalonePlyx && location.pathname === "/");

  // In standalone mode, the app renders ONLY Plyx routes. Anything else
  // bounces to /plyx so visitors can never accidentally land on AfriMarket.
  // Admin paths + login + /fulfil are exempt — they're internal tools and
  // must stay reachable even if the admin's browser once entered ?standalone=plyx.
  const _standaloneExempt = ["/plyx", "/admin", "/fulfil", "/login", "/register"];
  if (
    standalonePlyx
    && !_standaloneExempt.some((p) => location.pathname.startsWith(p))
  ) {
    return <Navigate to="/plyx" replace />;
  }

  return (
    <>
      {isPlyx ? <PlyxSEO /> : <AfriMarketSEO />}
      {!isPlyx && <AfriToPlyxRibbon />}
      {!isPlyx && <Navbar />}
      {isPlyx && !isPlyxFeed && <PlyxHeader />}
      <main className={isPlyx ? "" : "min-h-[60vh]"}>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/plyx" element={<PlyxFeed />} />
          <Route path="/plyx/about" element={<PlyxLanding />} />
          <Route path="/plyx/login" element={<PlyxLogin />} />
          <Route path="/plyx/register" element={<PlyxRegister />} />
          <Route path="/plyx/admin/login" element={<PlyxAdminLogin />} />
          <Route path="/plyx/forgot" element={<PlyxForgot />} />
          <Route path="/plyx/reset" element={<PlyxReset />} />
          <Route path="/plyx/upload" element={<PlyxUpload />} />
          <Route path="/plyx/settings" element={<PlyxSettings />} />
          <Route path="/plyx/wallet" element={<PlyxWallet />} />
          <Route path="/plyx/shop/manage" element={<PlyxShopManage />} />
          <Route path="/plyx/u/:handle/shop" element={<PlyxShop />} />
          <Route path="/plyx/challenges" element={<PlyxChallenges />} />
          <Route path="/plyx/challenges/new" element={<PlyxChallengeNew />} />
          <Route path="/plyx/challenges/:id" element={<PlyxChallengeDetail />} />
          <Route path="/plyx/leaderboard" element={<PlyxLeaderboard />} />
          <Route path="/plyx/makers" element={<PlyxMakers />} />
          <Route path="/plyx/makers/:id" element={<PlyxMakerDetail />} />
          <Route path="/plyx/u/:handle" element={<PlyxProfile />} />
          <Route path="/browse" element={<Browse />} />
          <Route path="/shopper" element={<AIShopperPage />} />
          <Route path="/map" element={<MapView />} />
          <Route path="/logos" element={<LogoCandidates />} />
          <Route path="/stats" element={<PublicStats />} />
          <Route path="/leaderboard" element={<ReferralLeaderboard />} />
          <Route path="/buyer-guarantee" element={<BuyerGuarantee />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/cookies" element={<CookiePolicy />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/orders/:ref/track" element={<OrderTrack />} />
          <Route path="/makers" element={<Makers />} />
          <Route path="/makers/new" element={<AuthGuard><MakerNew /></AuthGuard>} />
          <Route path="/makers/:id" element={<MakerDetail />} />
          <Route path="/movies" element={<Movies />} />
          <Route path="/movies/new" element={<AuthGuard><MovieNew /></AuthGuard>} />
          <Route path="/movies/:id" element={<MovieDetail />} />
          <Route path="/shops" element={<Shops />} />
          <Route path="/shop/:slug" element={<ShopDetail />} />
          <Route path="/dashboard/shop" element={<AuthGuard><ShopManage /></AuthGuard>} />
          <Route path="/dashboard/developer" element={<AuthGuard><DeveloperKeys /></AuthGuard>} />
          <Route path="/developers" element={<DeveloperDocs />} />
          <Route path="/share-ad" element={<ShareAd />} />
          <Route path="/african-fashion" element={<CategoryLanding />} />
          <Route path="/african-beauty" element={<CategoryLanding />} />
          <Route path="/african-food" element={<CategoryLanding />} />
          <Route path="/african-services" element={<CategoryLanding />} />
          <Route path="/african-home" element={<CategoryLanding />} />
          <Route path="/african-gifts" element={<CategoryLanding />} />
          <Route path="/admin/revenue" element={<AdminRevenue />} />
          <Route path="/admin/plyx" element={<AdminPlyxDashboard />} />
          <Route path="/admin/plyx/nps" element={<AuthGuard><AdminPlyxNps /></AuthGuard>} />
          <Route path="/admin/plyx/health" element={<AuthGuard><AdminPlyxHealth /></AuthGuard>} />
          <Route path="/admin/plyx/audit" element={<AdminPlyxAudit />} />
          <Route path="/listing/:id" element={<ListingDetail />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/pro" element={<Pro />} />
          <Route path="/verify" element={<AuthGuard><Verify /></AuthGuard>} />
          <Route path="/admin" element={<AuthGuard><Admin /></AuthGuard>} />
          <Route path="/fulfil" element={<AuthGuard><FulfilOrders /></AuthGuard>} />
          <Route path="/admin/complaints" element={<AuthGuard><AdminComplaints /></AuthGuard>} />
          <Route path="/complaints" element={<AuthGuard><Complaints /></AuthGuard>} />
          <Route path="/complaints/new" element={<AuthGuard><ComplaintNew /></AuthGuard>} />
          <Route path="/complaints/:id" element={<AuthGuard><ComplaintDetail /></AuthGuard>} />
          <Route path="/create" element={<AuthGuard><CreateListing /></AuthGuard>} />
          <Route path="/dashboard" element={<AuthGuard><Dashboard /></AuthGuard>} />
          <Route path="/dashboard/messages" element={<AuthGuard><Messages /></AuthGuard>} />
          <Route path="/dashboard/payouts" element={<AuthGuard><Payouts /></AuthGuard>} />
          <Route path="/dashboard/purchases" element={<AuthGuard><Purchases /></AuthGuard>} />
          <Route path="/dashboard/sales" element={<AuthGuard><Sales /></AuthGuard>} />
          <Route path="/dashboard/my-listings" element={<Navigate to="/dashboard" replace />} />
          {/* ── Location-first navigation (must come AFTER all static routes) ── */}
          <Route path="/locations" element={<LocationsIndex />} />
          <Route path="/near-me" element={<NearMe />} />
          <Route path="/:country" element={<LocationLanding />} />
          <Route path="/:country/:city" element={<LocationLanding />} />
          <Route path="/:country/:city/:area" element={<LocationLanding />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      {!isPlyx && <Footer />}
      {!isPlyx && <AIShopperBubble />}
      {!isPlyx && <InstallAppPrompt />}
      {!isPlyx && <CookieConsentBanner />}
      {!isPlyx && <NewsletterPopup />}
    </>
  );
};

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <AuthProvider>
            <PlyxAuthProvider>
              <CurrencyProvider>
                <AppRouter />
                <Toaster position="top-right" richColors />
              </CurrencyProvider>
            </PlyxAuthProvider>
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
